import type { DayRange } from '../model/range';

export type DayWindow = { fromDay: number; toDay: number };

/** How close to an edge the visible window gets before the range grows. */
export const EXTEND_THRESHOLD_DAYS = 7;

/**
 * Which days the header actually has to render: every day whose column falls under the scrollport
 * at all, occluded or not.
 *
 * The timeline's origin sits `sidebarWidth` in from the canvas's left edge, so the day at
 * `scrollLeft` is `(scrollLeft - sidebarWidth) / pxPerDay` columns past the range start.
 *
 * The leading `sidebarWidth` used NOT to be counted: the row-header column is opaque and sticky at
 * the scrollport's left edge, so those columns were hidden and rendering them was waste. The
 * timeline header no longer stops at that column — its day ruler runs the full width of the
 * scrollport, on top of the sidebar (`timeline-header.module.scss`) — so the days under it are
 * visible and have to be in the window. Leaving the old `+ sidebarWidth` bias in place left the
 * ruler blank over the sidebar at any zoom where `overscanDays` happened to be worth less than
 * `sidebarWidth` in pixels, which at the month zoom is most of them.
 */
export const visibleDayWindow = ({
  scrollLeft,
  viewportWidth,
  sidebarWidth,
  pxPerDay,
  range,
  overscanDays = 0,
}: {
  scrollLeft: number;
  viewportWidth: number;
  sidebarWidth: number;
  pxPerDay: number;
  range: DayRange;
  overscanDays?: number;
}): DayWindow => {
  const timelineWidth = Math.max(0, viewportWidth - sidebarWidth);

  const first = range.startDay + Math.floor((scrollLeft - sidebarWidth) / pxPerDay) - overscanDays;
  const last = range.startDay + Math.ceil((scrollLeft + timelineWidth) / pxPerDay) + overscanDays;

  const fromDay = Math.min(Math.max(first, range.startDay), range.endDay);
  const toDay = Math.max(Math.min(last, range.endDay), fromDay);

  return { fromDay, toDay };
};

/**
 * Replaces the old pair of IntersectionObservers on the first and sixth-from-last month
 * (`components/gantt-chart/components/chart-header.tsx:64-85`), which fired against DOM nodes
 * and needed a manual scrollLeft correction afterwards.
 */
export const extensionSideFor = (window: DayWindow, range: DayRange): 'start' | 'end' | null => {
  if (window.fromDay - range.startDay < EXTEND_THRESHOLD_DAYS) return 'start';
  if (range.endDay - window.toDay < EXTEND_THRESHOLD_DAYS) return 'end';

  return null;
};
